import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import Reveal from "@/components/Reveal";
import SectionHeading from "@/components/SectionHeading";
import { ABOUT_LEAD, ABOUT_SHORT, ABOUT_FULL } from "@/content";

function StoryBlock({ block, index }) {
  if (block.type === "quote") {
    return (
      <blockquote
        data-testid={`story-quote-${index}`}
        className="my-10 border-l-2 border-ink pl-6 md:pl-10"
      >
        <p className="font-spartan font-bold text-xl md:text-2xl leading-snug tracking-tight">
          “{block.text}”
        </p>
        {block.attribution && (
          <cite className="mt-3 block text-xs tracking-[0.25em] uppercase not-italic text-fog">
            — {block.attribution}
          </cite>
        )}
      </blockquote>
    );
  }
  if (block.type === "lines") {
    return (
      <div data-testid={`story-lines-${index}`} className="my-8 space-y-1">
        {block.items.map((line) => (
          <p key={line} className="font-spartan font-bold text-lg md:text-xl tracking-tight">
            {line}
          </p>
        ))}
      </div>
    );
  }
  return <p className="text-base md:text-lg leading-relaxed text-charcoal/90">{block.text}</p>;
}

export default function About() {
  const [open, setOpen] = useState(false);

  return (
    <section id="about" data-testid="about-section" className="py-24 md:py-36 px-6 md:px-12">
      <div className="max-w-7xl mx-auto">
        <SectionHeading number="01" title="About" testId="about-heading" />

        <div className="mt-14 md:mt-20 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-start">
          <Reveal className="lg:col-span-5" delay={0.1}>
            <div className="relative lg:sticky lg:top-28">
              <div className="absolute -top-4 -left-4 w-full h-full border border-ink/25" aria-hidden="true" />
              <img
                src="/assets/Isaiah-0332.webp"
                alt="David Rosenblum leading worship with one arm raised"
                data-testid="about-portrait"
                loading="lazy"
                className="relative w-full aspect-[4/5] object-cover grayscale-[15%]"
              />
            </div>
          </Reveal>

          <div className="lg:col-span-7 lg:pt-4">
            <Reveal delay={0.15}>
              <p data-testid="about-intro" className="text-base md:text-lg leading-relaxed text-charcoal/90 max-w-2xl">
                <strong className="font-semibold text-ink">David Rosenblum</strong>
                {ABOUT_LEAD.replace("David Rosenblum", "")}
              </p>
            </Reveal>
            {ABOUT_SHORT.map((paragraph, i) => (
              <Reveal key={i} delay={0.2 + i * 0.05}>
                <p className="mt-7 text-base md:text-lg leading-relaxed text-charcoal/90 max-w-2xl">
                  {paragraph}
                </p>
              </Reveal>
            ))}
            <Reveal delay={0.3}>
              <p className="mt-10 font-spartan font-extrabold uppercase tracking-tight text-3xl md:text-4xl">
                People Over Platforms.
              </p>
              <p className="mt-2 text-sm md:text-base text-fog">
                Because worship was never ultimately about the platform. The destination is Him.
              </p>
            </Reveal>
            <Reveal delay={0.35}>
              <button
                type="button"
                data-testid="about-story-toggle"
                aria-expanded={open}
                onClick={() => setOpen(!open)}
                className="mt-10 inline-flex items-center gap-3 text-xs font-semibold tracking-[0.22em] uppercase border-b border-ink pb-1 hover:gap-5 transition-[gap] duration-300"
              >
                {open ? "Close David’s full story" : "Read David’s full story"}{" "}
                <span aria-hidden="true">{open ? "↑" : "→"}</span>
              </button>
            </Reveal>
          </div>
        </div>

        <AnimatePresence initial={false}>
          {open && (
            <motion.div
              key="full-story"
              data-testid="about-full-story"
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: "auto", opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
              className="overflow-hidden"
            >
              <div className="mt-16 md:mt-24 border-t border-ink/15 pt-14 md:pt-20 max-w-3xl space-y-6">
                {ABOUT_FULL.map((block, i) => (
                  <StoryBlock key={i} block={block} index={i} />
                ))}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </section>
  );
}
